import { useContext, useEffect, useLayoutEffect, useState, type ReactNode, type RefObject, type UIEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import {
  ArrowDown,
  ArrowUp,
  Check,
  CircleHelp,
  Copy,
  Ellipsis,
  Info,
  Maximize,
  MessageSquareMore,
  PanelRight,
  Pencil,  PictureInPicture2,
  RefreshCw,
  RotateCcw,
  Sparkles,
  SquarePen,
  Square,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { toast } from "sonner";
import { Kbd } from "@/components/shadcn/kbd";
import { useClipboard } from "../../../hooks/useClipboard";
import { LayoutContext } from "../../../contexts/LayoutContext";
import { KlipboardIcon } from "../../logos/KlipboardIcon";
import { blocksToPlainText, formatMessageTime } from "./chatMessageUtils";
import { AiChart } from "./AiChart";
import { AiMarkdown, TABLE_CELL_CLASS } from "./AiMarkdown";
import { AiTable } from "./AiTable";
import { ReplySteps } from "./ReplySteps";
import type { ChatBlock } from "./chatBlocks";
import { MAX_MESSAGE_CHARS, MESSAGE_COUNTER_THRESHOLD, type ChatHeaderViewMode, type ChatViewMode } from "./chatConstants";
import type { ChatItem, ChatItemStatus, ChatRole, ReplyProgress } from "./chatSession";
import { BrailleSpinner, ReplyProgress as ReplyProgressView } from "./ReplyProgress";
import { CHAT_LENGTH_NOTICE_ID } from "./ChatLengthStatus";

export const SUGGESTED_PROMPTS = [
  "Show total sales for last month.",
  "How many customers do I have in London?",
  "Which accounts should I follow up with this week?",
  "What can you do?",
];

// Thinking indicator
const THINKING_PHASES: { afterSeconds: number; label: string }[] = [
  { afterSeconds: 0, label: "Thinking" },
  { afterSeconds: 4, label: "Working on it" },
  { afterSeconds: 10, label: "Still with you" },
  { afterSeconds: 20, label: "Thanks for your patience" },
  { afterSeconds: 40, label: "This one is taking a little longer" },
];

const getThinkingLabel = (elapsedSeconds: number): string =>
  THINKING_PHASES.reduce(
    (label, phase) => (elapsedSeconds >= phase.afterSeconds ? phase.label : label),
    THINKING_PHASES[0].label
  );

const ThinkingIndicator = () => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000
    );
    return () => window.clearInterval(timer);
  }, []);

  return (
    <span className="inline-flex items-center gap-2">
      <BrailleSpinner />
      <span className="text-slate-500 text-xs italic">{getThinkingLabel(elapsedSeconds)}</span>
    </span>
  );
};

// Message bubble component
const MESSAGE_ACTION_BUTTON =
  "inline-flex size-6 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 focus-visible:outline-2 focus-visible:outline-slate-400 disabled:cursor-not-allowed disabled:opacity-40";

const MessageMeta = ({
  role,
  blocks,
  createdAt,
  canRegenerate,
  isStreaming,
  onRegenerate,
  canEdit,
  onEdit,
}: {
  role: ChatRole;
  blocks: ChatBlock[];
  createdAt?: string;
  canRegenerate: boolean;
  isStreaming: boolean;
  onRegenerate: () => void;
  canEdit: boolean;
  onEdit: () => void;
}) => {
  const { copyToClipboard } = useClipboard();
  const [copied, setCopied] = useState(false);
  const time = formatMessageTime(createdAt);

  const handleCopy = async () => {
    const ok = await copyToClipboard(blocksToPlainText(blocks));
    if (!ok) {
      toast.error("Unable to copy to clipboard");
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const hasContent = blocks.length > 0;
  return (
    <div
      className={`mt-1 flex items-center gap-1 text-xs text-slate-400 ${role === "user" ? "justify-end" : ""} ${
        role === "assistant" && !canRegenerate ? "opacity-0 transition-opacity group-hover/message:opacity-100 group-focus-within/message:opacity-100" : ""
      }`}
    >
      {time ? (
        <time dateTime={createdAt} title={time.title}>
          {time.label}
        </time>
      ) : null}
      {role === "user" && canEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className={`${MESSAGE_ACTION_BUTTON} opacity-0 group-hover/message:opacity-100 group-focus-within/message:opacity-100 focus-visible:opacity-100`}
          aria-label="Edit message"
          title="Edit message"
        >
          <Pencil className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
      {role === "user" && hasContent ? (
        <button type="button" onClick={handleCopy} className={MESSAGE_ACTION_BUTTON} aria-label="Copy message" title="Copy message">
          {copied ? <Check className="size-3.5" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
        </button>
      ) : null}
      {role === "assistant" && hasContent && !isStreaming ? (
        <>
          <button type="button" onClick={handleCopy} className={MESSAGE_ACTION_BUTTON} aria-label="Copy reply" title="Copy reply">
            {copied ? <Check className="size-3.5" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
          </button>
          {canRegenerate ? (
            <button type="button" onClick={onRegenerate} className={MESSAGE_ACTION_BUTTON} aria-label="Regenerate reply" title="Regenerate reply">
              <RefreshCw className="size-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const ReplyStatusNote = ({ status, onRetry }: { status: ChatItemStatus; onRetry?: () => void }) => {
  const message = status.kind === "stopped" ? "You stopped this reply." : status.error.message;
  const canRetry = onRetry !== undefined && (status.kind === "stopped" || status.error.retryable);

  return (
    <div
      role="note"
      className="mt-3 flex items-start gap-2 rounded-lg bg-slate-100 px-3 py-2 text-slate-600"
    >
      <Info className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden="true" />
      <span className="min-w-0 flex-1">{message}</span>
      {canRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-200 focus-visible:outline-2 focus-visible:outline-slate-400"
        >
          <RefreshCw className="size-3" aria-hidden="true" />
          Retry
        </button>
      ) : null}
    </div>
  );
};

const ChatMessage = ({
  role,
  blocks,
  createdAt,
  status,
  progress,
  steps,
  canRegenerate = false,
  canRetry = false,
  isStreaming = false,
  onRegenerate = () => undefined,
  onRetry = () => undefined,
  canEdit = false,
  onEdit = () => undefined,
}: {
  role: ChatRole;
  blocks: ChatBlock[];
  createdAt?: string;
  status?: ChatItemStatus;
  progress?: ReplyProgress;
  steps?: string[];
  canRegenerate?: boolean;
  canRetry?: boolean;
  isStreaming?: boolean;
  onRegenerate?: () => void;
  onRetry?: () => void;
  canEdit?: boolean;
  onEdit?: () => void;
}) => (
  <div
    role="article"
    aria-label={role === "user" ? "You" : "Vecta"}
    className={`group/message flex ${role === "user" ? "justify-end" : "justify-start"}`}
  >
    <div className={role === "user" ? "flex max-w-[80%] flex-col" : "w-full"}>
    <div
      className={`text-sm ${
        role === "user"
          ? "rounded-2xl rounded-br-md bg-blue-600 px-4 py-2.5 leading-snug text-white"
          : "w-full px-1 leading-relaxed text-slate-800"
      }`}
    >
      {role === "assistant" && blocks.length === 0 && isStreaming && !progress?.pendingKind && !progress?.statusLabel ? (
        <ThinkingIndicator />
      ) : null}
      <div className="space-y-3">
        {blocks.map((block, index) => {
          switch (block.type) {
            case "text":
              return (
                role === "assistant" ? (
                  <AiMarkdown key={index} content={block.content} />
                ) : (
                  <p key={index} className="whitespace-pre-wrap">
                    {block.content}
                  </p>
                )
              );
            case "chart":
              return (
                <AiChart key={index} chartType={block.chartType} title={block.title} series={block.series} />
              );
            case "customer_link":
              return (
                <a
                  key={index}
                  href={block.href}
                  className={`inline-flex items-center gap-1.5 underline underline-offset-2 ${
                    role === "user"
                      ? "text-white hover:text-blue-100"
                      : "text-kPurple-600 hover:text-kPurple-800"
                  }`}
                >
                  <FontAwesomeIcon icon={faBuilding} className="size-3 shrink-0 opacity-70" />
                  {block.label}
                </a>
              );
            case "table":
              return (
                <div key={index} className="space-y-2">
                  {block.title ? <p className="font-semibold">{block.title}</p> : null}
                  <AiTable title={block.title}>
                    <thead>
                      <tr>
                        {block.columns.map((column) => (
                          <th key={column} className="border-b border-slate-300 px-2 py-1 font-semibold">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {block.rows.map((row, rowIndex) => (
                        <tr key={`${index}-${rowIndex}`}>
                          {row.map((cell, cellIndex) => (
                            <td key={`${index}-${rowIndex}-${cellIndex}`} className={TABLE_CELL_CLASS}>
                              <AiMarkdown content={cell} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </AiTable>
                </div>
              );
            case "list":
              return (
                <div key={index} className="space-y-1">
                  {block.title ? <p className="font-semibold">{block.title}</p> : null}
                  <ul className="list-disc space-y-1 pl-6">
                    {block.items.map((item, itemIndex) => (
                      <li key={`${index}-${itemIndex}`}>
                        <AiMarkdown content={item} />
                      </li>
                    ))}
                  </ul>
                </div>
              );
            case "warning":
              return (
                <div
                  key={index}
                  role="note"
                  className={`flex items-start gap-2 rounded-lg px-3 py-2 ${
                    role === "user"
                      ? "bg-white/10 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <Info className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden="true" />
                  <span>{block.content}</span>
                </div>
              );
            case "done":
              return null;
            default:
              return null;
          }
        })}
      </div>
      {role === "assistant" && isStreaming && (blocks.length > 0 || progress?.pendingKind || progress?.statusLabel) ? (
        <div className={blocks.length > 0 ? "mt-3" : ""}>
          <ReplyProgressView
            pendingKind={progress?.pendingKind ?? null}
            statusLabel={progress?.statusLabel ?? null}
          />
        </div>
      ) : null}
      {role === "assistant" && status ? <ReplyStatusNote status={status} onRetry={canRetry ? onRetry : undefined} /> : null}
      {role === "assistant" && !isStreaming ? <ReplySteps steps={steps} /> : null}
    </div>
    <MessageMeta
      role={role}
      blocks={blocks}
      createdAt={createdAt}
      canRegenerate={canRegenerate}
      isStreaming={isStreaming}
      onRegenerate={onRegenerate}
      canEdit={canEdit}
      onEdit={onEdit}
    />
    </div>
  </div>
);

export const ChatGreeting = () => {
  const layout = useContext(LayoutContext);
  const firstName = layout?.data.username?.trim().split(" ")[0];
  return (
    <>
      <KlipboardIcon size={32} />
      <h2 className="text-xl font-semibold text-slate-900">
        {firstName ? `Hi ${firstName}, what can I help you with today?` : "What can I help you with today?"}
      </h2>
      <p className="-mt-2 text-sm text-slate-500">I can help with CRM, sales analysis and BI.</p>
    </>
  );
};

export const ChatPrompts = ({ onPromptSelect }: { onPromptSelect: (prompt: string) => void }) => (
  <div className="flex flex-col gap-0.5">
    {SUGGESTED_PROMPTS.map((prompt) => (
      <button
        key={prompt}
        type="button"
        onClick={() => onPromptSelect(prompt)}
        className="group flex items-center gap-3 rounded-lg px-1.5 py-1.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-200/70"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600">
          <MessageSquareMore className="size-4" aria-hidden="true" />
        </span>
        <span>{prompt}</span>
      </button>
    ))}
  </div>
);

export const ChatRestoring = () => (
  <div role="status" className="flex flex-col gap-4 pb-2">
    <KlipboardIcon size={32} />
    <span className="inline-flex items-center gap-2">
      <BrailleSpinner />
      <span className="text-sm text-slate-500">Reconnecting to your last chat…</span>
    </span>
    <div className="space-y-2" aria-hidden="true">
      <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200" />
    </div>
  </div>
);

const ChatEmptyState = ({ onPromptSelect }: { onPromptSelect: (prompt: string) => void }) => (
  <div className="mt-auto flex flex-col gap-4 pb-2">
    <ChatGreeting />
    <ChatPrompts onPromptSelect={onPromptSelect} />
  </div>
);

export const JumpToLatestButton = ({ onClick, className }: { onClick: () => void; className: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-md hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-slate-400 ${className}`}
  >
    <ArrowDown className="size-3.5" aria-hidden="true" />
    Jump to latest
  </button>
);

// Messages container
export const ChatMessages = ({
  messages,
  scrollRef,
  onScroll,
  showJumpToLatest,
  onJumpToLatest,
  onPromptSelect,
  isSending,
  onRegenerate,
  onRetry,
  pageScroll = false,
  isRestoring = false,
  editableMessageId = null,
  onEditLast = () => undefined,
}: {
  editableMessageId?: number | null;
  onEditLast?: () => void;
  pageScroll?: boolean;
  isRestoring?: boolean;
  messages: ChatItem[];
  scrollRef: RefObject<HTMLDivElement>;
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  showJumpToLatest: boolean;
  onJumpToLatest: () => void;
  onPromptSelect: (prompt: string) => void;
  isSending: boolean;
  onRegenerate: () => void;
  onRetry: () => void;
}) => (
  <div className={pageScroll ? "flex flex-col" : "relative flex min-h-0 flex-1 flex-col"}>
  <div
    ref={scrollRef}
    onScroll={onScroll}
    role="log"
    aria-live="off"
    aria-label="Conversation"
    aria-busy={isSending}
    tabIndex={0}
    className={
      pageScroll
        ? "flex flex-col focus-visible:outline-2 focus-visible:outline-slate-400"
        : "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pr-1 focus-visible:outline-2 focus-visible:outline-slate-400"
    }
  >
    {messages.length === 0 ? (
      isRestoring ? (
        <div className="mt-auto"><ChatRestoring /></div>
      ) : (
        <ChatEmptyState onPromptSelect={onPromptSelect} />
      )
    ) : (
      <div className="space-y-3">
        {messages.map((msg, index) => {
          const isLast = index === messages.length - 1;
          return (
            <ChatMessage
              key={msg.id}
              role={msg.role}
              blocks={msg.blocks}
              createdAt={msg.createdAt}
              status={msg.status}
              progress={msg.progress}
              steps={msg.steps}
              isStreaming={isLast && isSending}
              canRegenerate={isLast && msg.role === "assistant" && !isSending && !msg.status}
              canRetry={isLast && !isSending}
              onRegenerate={onRegenerate}
              onRetry={onRetry}
              canEdit={editableMessageId === msg.id}
              onEdit={onEditLast}
            />
          );
        })}
      </div>
    )}
  </div>
  {showJumpToLatest && <JumpToLatestButton onClick={onJumpToLatest} className="absolute bottom-2 left-1/2 -translate-x-1/2" />}
  </div>
);

// Input section
const MAX_TEXTAREA_HEIGHT_PX = 212;
export const ChatInput = ({
  value,
  onChange,
  onSend,
  onStop,
  onNewChat,
  disabled,
  isSending,
  textareaRef,
  variant = "docked",
  limitReached = false,
  lengthIndicator = null,
  editing = false,
  onCancelEdit = () => undefined,
  onEditLast,
}: {
  editing?: boolean;
  onCancelEdit?: () => void;
  onEditLast?: () => void;
  limitReached?: boolean;
  lengthIndicator?: ReactNode;
  textareaRef: RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  onNewChat: () => void;
  disabled: boolean;
  isSending: boolean;
  variant?: "docked" | "hero";
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled) onSend();
      return;
    }

    if (e.key === "Escape" && editing) {
      e.preventDefault();
      onCancelEdit();
      return;
    }

    if (e.key === "ArrowUp" && value === "" && onEditLast) {
      e.preventDefault();
      onEditLast();
    }
  };

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    if (el.scrollHeight > 0) el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [value, textareaRef]);

  const isHero = variant === "hero";
  const showCounter = value.length >= MESSAGE_COUNTER_THRESHOLD;
  const atCap = value.length >= MAX_MESSAGE_CHARS;

  return (
    <div className={isHero ? "" : "mt-auto pt-3"}>
      <div className={isHero ? "sr-only" : "mb-2 flex items-center justify-between gap-3"}>
        <label htmlFor="chat-message" className="block text-xs font-medium text-slate-600">
          Message
        </label>
        {isHero ? null : lengthIndicator}
        {isHero ? null : (
          <button
            type="button"
            onClick={onNewChat}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-slate-400"
          >
            New chat
          </button>
        )}
      </div>

      {editing ? (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-xs text-blue-800">
          <span className="flex min-w-0 items-center gap-1.5">
            <Pencil className="size-3 shrink-0" aria-hidden="true" />
            Editing your last message
          </span>
          <button
            type="button"
            onClick={onCancelEdit}
            className="shrink-0 rounded-md px-2 py-0.5 font-medium hover:bg-blue-100 focus-visible:outline-2 focus-visible:outline-blue-400"
          >
            Cancel
          </button>
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-300 bg-white shadow-sm focus-within:border-slate-500">
        <textarea
          ref={textareaRef}
          id="chat-message"
          rows={2}
          maxLength={MAX_MESSAGE_CHARS}
          value={value}
          placeholder={limitReached ? "Chat length limit reached" : "Ask anything…"}
          disabled={limitReached}
          aria-describedby={limitReached ? CHAT_LENGTH_NOTICE_ID : undefined}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="block max-h-[212px] w-full resize-none overflow-y-auto rounded-t-xl bg-transparent px-4 pt-3 text-sm text-slate-800 outline-none ring-0 disabled:cursor-not-allowed disabled:text-slate-400"
        />
        <div className="flex items-center justify-between gap-2 px-2 pb-2">
          {showCounter ? (
            <span
              className={`px-2 text-xs tabular-nums ${atCap ? "text-destructive" : "text-slate-500"}`}
              aria-live="polite"
            >
              {value.length.toLocaleString("en-US")} / {MAX_MESSAGE_CHARS.toLocaleString("en-US")}
            </span>
          ) : (
            <span />
          )}
          {isSending ? (
            <button
              type="button"
              onClick={onStop}
              aria-label="Stop reply"
              title="Stop reply"
              className="flex size-8 items-center justify-center rounded-md bg-slate-700 text-white transition-colors hover:bg-slate-900 focus-visible:outline-2 focus-visible:outline-slate-400"
            >
              <Square className="size-3.5" fill="currentColor" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={disabled}
              aria-label="Send message"
              className="flex size-8 items-center justify-center rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-blue-400 disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-slate-100"
            >
              <ArrowUp className="size-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-col items-center gap-1 text-center text-xs text-slate-400">
        <p>Vecta can make mistakes. Check important information.</p>
        <p className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5">
          <Kbd>Enter</Kbd> to send, <Kbd>Shift</Kbd> + <Kbd>Enter</Kbd> for a new line
        </p>
      </div>
    </div>
  );
};

// Header
const HEADER_ICON_BUTTON =
  "flex size-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 data-[state=open]:bg-slate-200";

const SUPPORT_CENTRE_URL = "https://vecta.net/en-gb/support-service-second-to-none";

const MENU_LABEL = "px-2 py-1.5 text-xs font-semibold text-slate-500";

export const ChatHeader = ({
  onClose,
  onNewChat,
  onResetSize,
  viewMode,
  onViewModeChange,
}: {
  onClose?: () => void;
  onNewChat: () => void;
  onResetSize?: () => void;
  viewMode?: ChatViewMode;
  onViewModeChange?: (mode: ChatHeaderViewMode) => void;
}) => (
  <div
    id="chat-toggle"
    className="flex w-full shrink-0 items-center justify-between gap-2 px-4 py-2 text-sm font-semibold text-slate-900"
  >
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex items-center gap-1.5">
        <Sparkles className="size-4 text-amber-400" fill="currentColor" aria-hidden="true" />
        Ask Vecta
      </span>
    </span>

    <div className="flex shrink-0 items-center gap-1">
      <button type="button" onClick={onNewChat} className={HEADER_ICON_BUTTON} aria-label="New chat" title="New chat">
        <SquarePen className="size-4" aria-hidden="true" />
      </button>

      {onViewModeChange ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={HEADER_ICON_BUTTON} aria-label="View" title="View">
              {viewMode === "floating"
                ? <PictureInPicture2 className="size-4" aria-hidden="true" />
                : <PanelRight className="size-4" aria-hidden="true" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-48">
            <DropdownMenuLabel className={MENU_LABEL}>View</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => onViewModeChange("floating")}>
              <PictureInPicture2 aria-hidden="true" />
              Floating
              {viewMode === "floating" && <Check className="ml-auto" aria-hidden="true" />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onViewModeChange("sidebar")}>
              <PanelRight aria-hidden="true" />
              Sidebar
              {viewMode === "sidebar" && <Check className="ml-auto" aria-hidden="true" />}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onViewModeChange("fullscreen")}>
              <Maximize aria-hidden="true" />
              Full screen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={HEADER_ICON_BUTTON} aria-label="More options" title="More options">
            <Ellipsis className="size-4" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          {onResetSize ? (
            <>
              <DropdownMenuItem onSelect={onResetSize}><RotateCcw aria-hidden="true" />Reset size</DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}
          <DropdownMenuItem asChild>
            <a href={SUPPORT_CENTRE_URL} target="_blank" rel="noopener noreferrer">
              <CircleHelp aria-hidden="true" />
              Get help
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {onClose ? (
        <button type="button" onClick={onClose} className={HEADER_ICON_BUTTON} aria-label="Close" title="Close">
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  </div>
);

